/**
 * Admin User Modal Component
 *
 * Dialog for creating and editing admin users.
 * Uses form validation with Zod schemas.
 */
import { useState, useEffect } from 'react';
import {
  cn,
  Button,
  Input,
  Label,
  Switch,
} from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@respectly/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
import { User, Loader2, Eye, EyeOff } from 'lucide-react';
import type {
  SuperAdminUser,
  SuperAdminRole,
  CreateSuperAdminUserDto,
  UpdateSuperAdminUserDto,
} from '@respectly/shared-types';
import { getRoleOptions } from './AdminRoleBadge';

// ============================================
// Form State
// ============================================

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  role: SuperAdminRole;
  phone: string;
  password: string;
  isActive: boolean;
}

const initialFormState: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'ReadOnlyAdmin',
  phone: '',
  password: '',
  isActive: true,
};

// ============================================
// Component Props
// ============================================

export interface AdminUserModalProps {
  user?: SuperAdminUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    data: CreateSuperAdminUserDto | UpdateSuperAdminUserDto
  ) => Promise<boolean>;
  isLoading?: boolean;
}

// ============================================
// Component
// ============================================

export function AdminUserModal({
  user,
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: AdminUserModalProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [showPassword, setShowPassword] = useState(false);

  const isEditing = !!user;
  const roleOptions = getRoleOptions();

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (open && user) {
      setForm({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        phone: user.phone ?? '',
        password: '',
        isActive: user.isActive,
      });
    } else if (open) {
      setForm(initialFormState);
    }
    setErrors({});
    setShowPassword(false);
  }, [open, user]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!form.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!isEditing && !form.password) {
      newErrors.password = 'Password is required';
    } else if (!isEditing && form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const data: CreateSuperAdminUserDto | UpdateSuperAdminUserDto = isEditing
      ? {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          role: form.role,
          phone: form.phone || undefined,
          isActive: form.isActive,
        }
      : {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          role: form.role,
          phone: form.phone || undefined,
          password: form.password,
          isActive: form.isActive,
        };

    const success = await onSubmit(data);
    if (success) {
      onOpenChange(false);
    }
  };

  // Handle input change
  const handleChange = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
              <User className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <DialogTitle>
                {isEditing ? 'Edit Admin User' : 'Create Admin User'}
              </DialogTitle>
              <DialogDescription>
                {isEditing
                  ? 'Update the admin user details below.'
                  : 'Fill in the details to create a new admin user.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="John"
                className={cn(errors.firstName && 'border-red-500')}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Doe"
                className={cn(errors.lastName && 'border-red-500')}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="john.doe@example.com"
              className={cn(errors.email && 'border-red-500')}
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Password (only for create) */}
          {!isEditing && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Min. 8 characters"
                  className={cn(errors.password && 'border-red-500', 'pr-10')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password}</p>
              )}
            </div>
          )}

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select
              value={form.role}
              onValueChange={(value) => handleChange('role', value as SuperAdminRole)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          {/* Active status */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="isActive" className="font-medium">
                Active Status
              </Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Inactive users cannot log in
              </p>
            </div>
            <Switch
              id="isActive"
              checked={form.isActive}
              onCheckedChange={(checked) => handleChange('isActive', checked)}
            />
          </div>
        </form>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditing ? (
              'Update User'
            ) : (
              'Create User'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
